import { Link } from "react-router";
import LocationShorthandPill from "@/components/tpr/LocationShorthandPill/LocationShorthandPill";
import BookingTypePill from "@/components/tpr/BookingTypePill/BookingTypePill";
import RoomSwatch from "@/components/tpr/RoomSwatch/RoomSwatch";
import { formatUtcDate, humanizeDuration } from "@/lib/utils";
import type { Booking } from "@/types/Booking";

interface OrgBookingRowProps {
  booking: Booking;
  /** When set, the booking reference becomes a link to this URL. */
  bookingHref?: string;
  /** When set, the member name becomes a link to this URL. */
  memberHref?: string;
  /** When true, show the booking duration (e.g. "1hr 30m"). */
  showDuration?: boolean;
  /** When true, render the room with its colour swatch instead of plain text. */
  showRoomSwatch?: boolean;
}

/**
 * One organisation-booking line: ref · member · location · date · [duration] · room · type.
 * Shared by OrgBookingsList (owner view) and OrgBookingsPanel (admin/PM detail page).
 * With no optional props it renders the plain owner-view row; the admin/PM page opts into
 * clickable ref/member links, duration and a room colour swatch via the props above.
 */
export default function OrgBookingRow({
  booking: bk,
  bookingHref,
  memberHref,
  showDuration = false,
  showRoomSwatch = false,
}: OrgBookingRowProps) {
  return (
    <li
      data-testid={`org-bookings-row-${bk.id}`}
      className="flex items-center gap-3 py-2 text-sm"
    >
      {bookingHref ? (
        <Link to={bookingHref} className="font-mono uppercase text-blue-600 hover:underline w-16 shrink-0">
          {bk.shortCode ?? "—"}
        </Link>
      ) : (
        <span className="font-mono uppercase text-gray-500 w-16 shrink-0">{bk.shortCode ?? "—"}</span>
      )}

      {memberHref ? (
        <Link to={memberHref} className="flex-1 truncate text-blue-600 hover:underline">
          {bk.member?.firstName} {bk.member?.lastName}
        </Link>
      ) : (
        <span className="flex-1 truncate">
          {bk.member?.firstName} {bk.member?.lastName}
        </span>
      )}

      {bk.location?.name ? (
        <LocationShorthandPill location={bk.location.name} shortCode={bk.location?.initials} />
      ) : null}

      <span className="text-muted-foreground shrink-0">
        {bk.date ? formatUtcDate(bk.date, false, true) : "—"}
      </span>

      {showDuration && bk.startTime && bk.endTime ? (
        <span className="text-muted-foreground shrink-0 tabular-nums">
          {humanizeDuration(bk.startTime, bk.endTime)}
        </span>
      ) : null}

      {showRoomSwatch ? (
        <span className="shrink-0">
          <RoomSwatch swatch={bk.room?.swatch ?? "bg-gray-300"} name={bk.room?.name ?? "—"} />
        </span>
      ) : (
        <span className="text-muted-foreground shrink-0">{bk.room?.name ?? "—"}</span>
      )}

      <BookingTypePill booking={bk} className="text-xs" />
    </li>
  );
}
